import React from 'react';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
  TooltipProps,
  Label,
} from 'recharts';
import { cn } from '../../lib/utils';
import { useChartPalette } from './chartColors';

export interface TailwindPieChartData {
  name: string;
  value: number;
  color?: string;
  // Allow consumer-defined extras (raw values, etc.) without losing type safety
  [key: string]: any;
}

export interface TailwindPieChartProps {
  data: TailwindPieChartData[];
  dataKey?: string;
  nameKey?: string;
  height?: number;
  width?: number | string;
  showLegend?: boolean;
  showTooltip?: boolean;
  showLabels?: boolean;
  innerRadius?: number | string; // For donut charts (0 = pie, >0 = donut). String like "40%" allowed.
  outerRadius?: number | string;
  paddingAngle?: number;
  className?: string;
  margin?: { top?: number; right?: number; bottom?: number; left?: number };
  customTooltip?: React.ComponentType<TooltipProps<any, any>>;
  centerLabel?: string; // For donut charts
  labelFormatter?: (entry: any) => React.ReactNode;
  labelLine?: boolean; // default false (inside renderer positions itself)
  tooltipValueFormatter?: (value: any, name?: string) => React.ReactNode | [React.ReactNode, React.ReactNode];
}

// Custom label renderer for pie slices (default behavior)
const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) => {
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  if (percent < 0.05) return null; // Don't show labels for slices < 5%

  return (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central"
      fontSize={12}
      fontWeight={600}
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

export default function TailwindPieChart({
  data,
  dataKey = 'value',
  nameKey = 'name',
  height = 400,
  width = '100%',
  showLegend = true,
  showTooltip = true,
  showLabels = true,
  innerRadius = 0,
  outerRadius = 120,
  paddingAngle = 2,
  className,
  margin,
  customTooltip,
  centerLabel,
  labelFormatter,
  labelLine = false,
  tooltipValueFormatter,
}: TailwindPieChartProps) {
  const palette = useChartPalette();
  const getColor = (index: number) => palette[index % palette.length];
  const labelProp = showLabels ? (labelFormatter ?? renderCustomLabel) : false;

  return (
    <div className={cn('w-full', className)}>
      <ResponsiveContainer width={width} height={height}>
        <PieChart margin={margin}>
          <Pie
            data={data}
            dataKey={dataKey}
            nameKey={nameKey}
            cx="50%"
            cy="50%"
            innerRadius={innerRadius as any}
            outerRadius={outerRadius as any}
            paddingAngle={paddingAngle}
            label={labelProp as any}
            labelLine={labelLine}
          >
            {data.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={entry.color || getColor(index)}
              />
            ))}
            {centerLabel && innerRadius !== 0 && (
              <Label
                value={centerLabel}
                position="center"
                fill="#1e293b"
                style={{ fontSize: '18px', fontWeight: 600 }}
              />
            )}
          </Pie>
          {showTooltip && (
            customTooltip ? (
              <Tooltip content={customTooltip} />
            ) : (
              <Tooltip
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #e2e8f0',
                  borderRadius: '0.5rem',
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                }}
                labelStyle={{ color: '#1e293b', fontWeight: 600 }}
                formatter={tooltipValueFormatter as any}
              />
            )
          )}
          {showLegend && (
            <Legend
              verticalAlign="bottom"
              height={36}
              iconType="circle"
            />
          )}
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}
